// AddEventModal.js
import React, { useState, useEffect } from 'react';
import {
    IonModal,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonLabel,
    IonInput,
    IonButton,
    IonItem,
    IonRow,
    IonCol,
    IonFooter,
    useIonAlert,
    IonIcon, IonButtons
} from '@ionic/react';
import Editor from '@monaco-editor/react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import moment from 'moment-timezone';

import {addCircleOutline, trashOutline, createOutline, star, arrowBack} from 'ionicons/icons';

// NOTE: keep these imports pointing to your existing helpers
import {
    actionToUploadEventDataWithPdf,
    actionToUpdateEventDataWithPdf,
    actionToGetEventsByFilter
} from '../apiHelpers/CommonAction';

import useStore from '../zustand/useStore';

const defaultOrg = 'Direct Tax Committee';

export default function AddEventModal({
                                          isOpen,
                                          onClose,
                                          setIsLoading,
                                          dataForEditing
                                      }) {
    const [form, setForm] = useState({
        title: '',
        topic: '',
        description: '',
        venue: '',
        event_date_time: null,
        event_end_date_time: null,
        location: '',
        organised_by: defaultOrg,
        program_html: '',
        price_for_ca: '',
        price_for_member: '',
        price_for_other: ''
    });

    // Experts: [{name, designation, imageFile, previewUrl}]
    const [experts, setExperts] = useState([
        { name: '', designation: '', imageFile: null, previewUrl: '' }
    ]);

    const [imageFile, setImageFile] = useState(null);
    const [pdfFile, setPdfFile] = useState(null);
    const [errors, setErrors] = useState({});
    const [showProgramEditor, setShowProgramEditor] = useState(false);
    const [presentAlert] = useIonAlert();
    const { resetEventFilters ,eventsDataState} = useStore();

    useEffect(() => {
        if (isOpen && !dataForEditing?.id) {
            // Fresh form defaults
            setForm({
                title: '',
                topic: '',
                description: '',
                venue: '',
                event_date_time: null,
                event_end_date_time: null,
                location: '',
                organised_by: defaultOrg,
                program_html: '',
                price_for_ca: '',
                price_for_member: '',
                price_for_other: ''
            });
            setExperts([{ name: '', designation: '', imageFile: null, previewUrl: '' }]);
            setImageFile(null);
            setPdfFile(null);
            setErrors({});
        }
    }, [isOpen, dataForEditing]);

    useEffect(() => {
        if (dataForEditing?.id) {

            setForm({
                title: dataForEditing.title || '',
                topic: dataForEditing.topic || '',
                description: dataForEditing.description || '',
                venue: dataForEditing.venue || '',
                event_date_time: moment(dataForEditing?.event_date_time).format('YYYY-MM-DD HH:mm:ss'),
                event_end_date_time: moment(dataForEditing?.event_end_date_time).format('YYYY-MM-DD HH:mm:ss'),
                location: dataForEditing.location || '',
                organised_by: dataForEditing.organised_by || defaultOrg,
                program_html: dataForEditing.program_html || '',
                price_for_ca: dataForEditing.price_for_ca ?? '',
                price_for_member: dataForEditing.price_for_member ?? '',
                price_for_other: dataForEditing.price_for_other ?? ''
            });

            setImageFile(null);
            setPdfFile(null);

            // Normalise experts if editing (if your API already returns experts array)
            const incomingExperts = Array.isArray(dataForEditing.experts)
                ? dataForEditing.experts
                : [];
            if (incomingExperts.length) {
                setExperts(
                    incomingExperts.map((ex) => ({
                        name: ex.name || '',
                        designation: ex.designation || '',
                        imageFile: null,
                        previewUrl: ex.image_url || ''
                    }))
                );
            } else {
                setExperts([{ name: '', designation: '', imageFile: null, previewUrl: '' }]);
            }
        }
    }, [dataForEditing]);

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value ?? '' }));
    };

    const handleExpertChange = (idx, field, value) => {
        setExperts((prev) => {
            const next = [...prev];
            next[idx][field] = value ?? '';
            return next;
        });
    };

    const handleExpertImage = (idx, file) => {
        setExperts((prev) => {
            const next = [...prev];
            next[idx].imageFile = file || null;
            if (file) {
                next[idx].previewUrl = URL.createObjectURL(file);
            }
            return next;
        });
    };

    const addExpertRow = () => {
        setExperts((prev) => [...prev, { name: '', designation: '', imageFile: null, previewUrl: '' }]);
    };

    const removeExpertRow = (idx) => {
        setExperts((prev) => prev.filter((_, i) => i !== idx));
    };

    const validate = () => {
        const newErrors = {};

        if (!form.title?.trim()) newErrors.title = 'Title is required';
        if (!form.topic?.trim()) newErrors.topic = 'Topic is required';
        if (!form.description?.trim()) newErrors.description = 'Description is required';
        if (!form.venue?.trim()) newErrors.venue = 'Venue is required';
        if (!form.event_date_time) newErrors.event_date_time = 'Event date time is required';
        if (!form.event_end_date_time) newErrors.event_end_date_time = 'Event end date time is required';
        if (!form.location?.trim()) newErrors.location = 'Location is required';
        if (!form.organised_by?.trim()) newErrors.organised_by = 'Organised By is required';
        if (!form.program_html?.trim()) newErrors.program_html = 'Detailed Program is required';

        // Experts validation: at least one fully/partially filled expert row,
        // and for each filled row, require name + designation + image (new or existing)
        const filled = experts.filter(
            (e) => (e.name && e.name.trim()) || (e.designation && e.designation.trim()) || e.imageFile || e.previewUrl
        );
        if (filled.length === 0) {
            newErrors.experts = 'Add at least one expert.';
        } else {
            filled.forEach((e, idx) => {
                if (!e.name?.trim()) newErrors[`experts_${idx}_name`] = 'Name is required';
                if (!e.designation?.trim()) newErrors[`experts_${idx}_designation`] = 'Designation is required';
                if (!e.imageFile && !e.previewUrl) newErrors[`experts_${idx}_image`] = 'Image is required';
            });
        }

        // Files required on create; optional on edit:
        if (!dataForEditing?.id) {
            if (!imageFile) newErrors.image = 'Banner image is required';
            if (!pdfFile) newErrors.pdf = 'PDF is required';
        }

        return newErrors;
    };

    const handleImageUpload = (e) => {
        const file = e.target.files?.[0];
        if (file) setImageFile(file);
    };

    const handlePdfUpload = (e) => {
        const file = e.target.files?.[0];
        if (file) setPdfFile(file);
    };

    const handleSubmit = () => {
        const v = validate();
        if (Object.keys(v).length) {
            setErrors(v);
            return;
        }

        setErrors({});
        setIsLoading?.(true);

        const fd = new FormData();
        fd.append('title', form.title);
        fd.append('topic', form.topic);
        fd.append('description', form.description);
        fd.append('venue', form.venue);
        fd.append('event_date_time', form.event_date_time);
        fd.append('event_end_date_time', form.event_end_date_time);
        fd.append('location', form.location);
        fd.append('organised_by', form.organised_by);
        fd.append('program_html', form.program_html);
        fd.append('price_for_ca', String(form.price_for_ca));
        fd.append('price_for_member', String(form.price_for_member));
        fd.append('price_for_other', String(form.price_for_other));

        // Experts meta + files
        const expertsMeta = experts
            .filter((e) => e.name?.trim() || e.designation?.trim() || e.imageFile || e.previewUrl)
            .map((e, i) => ({
                name: e.name?.trim() || '',
                designation: e.designation?.trim() || '',
                image_field_key: e.imageFile ? `expert_image_${i}` : null,
                existing_image_url: !e.imageFile ? (e.previewUrl || null) : null
            }));

        fd.append('experts_meta', JSON.stringify(expertsMeta));
        experts.forEach((e, i) => {
            if (e.imageFile) fd.append(`expert_image_${i}`, e.imageFile);
        });

        if (imageFile) fd.append('image', imageFile);
        if (pdfFile) fd.append('pdf', pdfFile);

        let apiCall;
        if (dataForEditing?.id) {
            fd.append('id', dataForEditing.id);
            apiCall = actionToUpdateEventDataWithPdf(fd);
        } else {
            apiCall = actionToUploadEventDataWithPdf(fd);
        }

        apiCall
            .then(() => {
                setIsLoading?.(false);
                resetEventFilters?.();
                if(dataForEditing?.id){
                    actionToGetEventsByFilter?.(false,{offset:eventsDataState?.offset})
                }else{
                    actionToGetEventsByFilter?.();
                }
                onClose?.();
            })
            .catch((err) => {
                setIsLoading?.(false);
                presentAlert({
                    header: 'Upload Failed',
                    message: err?.message || 'Something went wrong while saving the event.',
                    buttons: ['OK']
                });
            });
    };

    const quillModules = {
        toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            [{ header: 1 }, { header: 2 }],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ align: '' }, { align: 'center' }, { align: 'right' }, { align: 'justify' }],
            ['link', 'clean']
        ]
    };

    return (
        <>
            <IonModal
                isOpen={isOpen}
                onDidDismiss={onClose}
                className={"add_event_full_modal_popup"}
            >
                <IonHeader className="job_detail_sheet_header sheet_modal_header">
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonButton className={"close_job_post_button"} onClick={onClose}>
                                <IonIcon icon={arrowBack}/>
                            </IonButton>
                        </IonButtons>
                        <IonTitle>{dataForEditing?.id ? 'Edit Event' : 'Add Event'}</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent className="ion-padding bottom-2 no_padding_top">
                    <div>
                        {/* Title */}
                        <div className="rfp_submit_form_item">
                            <IonLabel>Title<IonIcon icon={star} className="required-star" /></IonLabel>
                            <IonInput
                                placeholder="Enter event title"
                                className="rfp_form_input"
                                value={form.title}
                                onIonInput={(e) => handleChange('title', e.detail.value)}
                            />
                            {errors.title && <p className="error-text">{errors.title}</p>}
                        </div>

                        {/* Topic */}
                        <div className="rfp_submit_form_item">
                            <IonLabel>Topic<IonIcon icon={star} className="required-star" /></IonLabel>
                            <IonInput
                                placeholder="Enter topic"
                                className="rfp_form_input"
                                value={form.topic}
                                onIonInput={(e) => handleChange('topic', e.detail.value)}
                            />
                            {errors.topic && <p className="error-text">{errors.topic}</p>}
                        </div>

                        {/* Organised By */}
                        <div className="rfp_submit_form_item">
                            <IonLabel>Organised By<IonIcon icon={star} className="required-star" /></IonLabel>
                            <IonInput
                                placeholder="Organised by"
                                className="rfp_form_input"
                                value={form.organised_by}
                                onIonInput={(e) => handleChange('organised_by', e.detail.value)}
                            />
                            {errors.organised_by && <p className="error-text">{errors.organised_by}</p>}
                        </div>

                        {/* Description */}
                        <div className="rfp_submit_form_item">
                            <IonLabel>Description<IonIcon icon={star} className="required-star" /></IonLabel>
                            <ReactQuill
                                theme="snow"
                                className="react_quill_in_news"
                                value={form.description}
                                onChange={(value) => handleChange('description', value)}
                                modules={quillModules}
                                style={{ backgroundColor: 'white' }}
                            />
                            {errors.description && <p className="error-text">{errors.description}</p>}
                        </div>

                        {/* Venue */}
                        <div className="rfp_submit_form_item">
                            <IonLabel>Venue<IonIcon icon={star} className="required-star" /></IonLabel>
                            <IonInput
                                placeholder="Enter venue"
                                className="rfp_form_input"
                                value={form.venue}
                                onIonInput={(e) => handleChange('venue', e.detail.value)}
                            />
                            {errors.venue && <p className="error-text">{errors.venue}</p>}
                        </div>

                        {/* Event Date */}
                        <div className="rfp_submit_form_item">
                            <IonLabel>Event Date Time<IonIcon icon={star} className="required-star" /></IonLabel>
                            <IonInput
                                type="datetime-local"
                                className="rfp_form_input"
                                value={form.event_date_time}
                                onIonInput={(e) => handleChange('event_date_time', e.detail.value)}
                            />
                            {errors.event_date_time && <p className="error-text">{errors.event_date_time}</p>}
                        </div>

                        <div className="rfp_submit_form_item">
                            <IonLabel>Event End Date Time<IonIcon icon={star} className="required-star" /></IonLabel>
                            <IonInput
                                type="datetime-local"
                                className="rfp_form_input"
                                format={"YYYY-MM-DD hh:mm a"}
                                value={form.event_end_date_time}
                                onIonInput={(e) => handleChange('event_end_date_time', e.detail.value)}
                            />
                            {errors.event_end_date_time && <p className="error-text">{errors.event_end_date_time}</p>}
                        </div>

                        {/* Location */}
                        <div className="rfp_submit_form_item">
                            <IonLabel>Location<IonIcon icon={star} className="required-star" /></IonLabel>
                            <IonInput
                                placeholder="Enter location"
                                className="rfp_form_input"
                                value={form.location}
                                onIonInput={(e) => handleChange('location', e.detail.value)}
                            />
                            {errors.location && <p className="error-text">{errors.location}</p>}
                        </div>

                        {/* Pricing (CA / Member / Other) */}
                        <div className="rfp_submit_form_item">
                            <IonLabel>Pricing<IonIcon icon={star} className="required-star" /></IonLabel>
                            <div className="grid-3">
                                <div>
                                    <IonLabel className="sub">CA</IonLabel>
                                    <IonInput
                                        type="number"
                                        inputMode="decimal"
                                        placeholder="e.g., 1500"
                                        className="rfp_form_input"
                                        value={form.price_for_ca}
                                        onIonInput={(e) => handleChange('price_for_ca', e.detail.value)}
                                    />
                                    {errors.price_for_ca && <p className="error-text">{errors.price_for_ca}</p>}
                                </div>
                                <div>
                                    <IonLabel className="sub">Member</IonLabel>
                                    <IonInput
                                        type="number"
                                        inputMode="decimal"
                                        placeholder="e.g., 1000"
                                        className="rfp_form_input"
                                        value={form.price_for_member}
                                        onIonInput={(e) => handleChange('price_for_member', e.detail.value)}
                                    />
                                    {errors.price_for_member && <p className="error-text">{errors.price_for_member}</p>}
                                </div>
                                <div>
                                    <IonLabel className="sub">Other</IonLabel>
                                    <IonInput
                                        type="number"
                                        inputMode="decimal"
                                        placeholder="e.g., 2000"
                                        className="rfp_form_input"
                                        value={form.price_for_other}
                                        onIonInput={(e) => handleChange('price_for_other', e.detail.value)}
                                    />
                                    {errors.price_for_other && <p className="error-text">{errors.price_for_other}</p>}
                                </div>
                            </div>
                        </div>

                        {/* Featured Faculty / Subject Matter Experts */}
                        <div className="rfp_submit_form_item">
                            <IonLabel>Featured Faculty / Subject Matter Experts<IonIcon icon={star} className="required-star" /></IonLabel>

                            {experts.map((ex, idx) => (
                                <div key={idx} className="expert-row">
                                    <div className="expert-grid">
                                        <IonInput
                                            placeholder="Name"
                                            className="rfp_form_input"
                                            value={ex.name}
                                            onIonInput={(e) => handleExpertChange(idx, 'name', e.detail.value)}
                                        />
                                        {errors[`experts_${idx}_name`] && <p className="error-text">{errors[`experts_${idx}_name`]}</p>}

                                        <IonInput
                                            placeholder="Designation"
                                            className="rfp_form_input"
                                            value={ex.designation}
                                            onIonInput={(e) => handleExpertChange(idx, 'designation', e.detail.value)}
                                        />
                                        {errors[`experts_${idx}_designation`] && (
                                            <p className="error-text">{errors[`experts_${idx}_designation`]}</p>
                                        )}

                                        <IonItem className="ion-no-padding">
                                            <input
                                                className="attach-pdf"
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => handleExpertImage(idx, e.target.files?.[0] || null)}
                                            />
                                        </IonItem>

                                        {ex.previewUrl && (
                                            <div className="expert-thumb">
                                                <img alt="expert" src={ex.previewUrl} />
                                            </div>
                                        )}

                                        {errors[`experts_${idx}_image`] && <p className="error-text">{errors[`experts_${idx}_image`]}</p>}
                                    </div>

                                    {experts.length > 1 && (
                                        <IonButton fill="clear" color="danger" onClick={() => removeExpertRow(idx)}>
                                            <IonIcon slot="start" icon={trashOutline} />
                                            Remove
                                        </IonButton>
                                    )}
                                </div>
                            ))}

                            <IonButton onClick={addExpertRow} fill="outline">
                                <IonIcon slot="start" icon={addCircleOutline} />
                                Add Expert
                            </IonButton>

                            {errors.experts && <p className="error-text">{errors.experts}</p>}
                        </div>

                        {/* Detailed Program (HTML) */}
                        <div className="rfp_submit_form_item">
                            <IonLabel>Detailed Program<IonIcon icon={star} className="required-star" /></IonLabel>
                            <IonButton fill="outline" onClick={() => setShowProgramEditor(true)}>
                                <IonIcon slot="start" icon={createOutline} />
                                Open HTML Editor
                            </IonButton>

                            <div className="program-preview-snippet">
                                {form.program_html?.trim() ? (
                                    <div
                                        className="program-snippet"
                                        dangerouslySetInnerHTML={{ __html: form.program_html }}
                                    />
                                ) : (
                                    <small>No content yet. Click “Open HTML Editor”.</small>
                                )}
                            </div>
                            {errors.program_html && <p className="error-text">{errors.program_html}</p>}
                        </div>

                        {/* Banner Image */}
                        <div className="rfp_submit_form_item">
                            <IonLabel>Upload Image{!dataForEditing?.id && <IonIcon icon={star} className="required-star" />}</IonLabel>
                            <IonItem className="ion-no-padding ion-margin-bottom">
                                <input className="attach-pdf" type="file" accept="image/*" onChange={handleImageUpload} />
                            </IonItem>
                            {dataForEditing?.id && !imageFile && dataForEditing.image_url && (
                                <small>Current: {dataForEditing.image_url}</small>
                            )}
                            {errors.image && <p className="error-text">{errors.image}</p>}
                        </div>

                        {/* PDF */}
                        <div className="rfp_submit_form_item">
                            <IonLabel>Upload PDF{!dataForEditing?.id && <IonIcon icon={star} className="required-star" />}</IonLabel>
                            <IonItem className="ion-no-padding ion-margin-bottom">
                                <input className="attach-pdf" type="file" accept="application/pdf" onChange={handlePdfUpload} />
                            </IonItem>
                            {dataForEditing?.id && !pdfFile && dataForEditing.pdf_url && (
                                <small>Current: {dataForEditing.pdf_url}</small>
                            )}
                            {errors.pdf && <p className="error-text">{errors.pdf}</p>}
                        </div>
                    </div>
                </IonContent>

                <IonFooter className="job_detail_sheet_footer ion-padding">
                    <IonToolbar>
                        <IonRow>
                            <IonCol>
                                <IonButton className="close_job_detail_button" expand="block" onClick={onClose}>
                                    Close
                                </IonButton>
                            </IonCol>
                            <IonCol>
                                <IonButton className="apply_job_button" expand="block" onClick={handleSubmit}>
                                    {dataForEditing?.id ? 'Update Event' : 'Submit Event'}
                                </IonButton>
                            </IonCol>
                        </IonRow>
                    </IonToolbar>
                </IonFooter>
            </IonModal>

            {/* Detailed Program Editor Modal */}
            <IonModal isOpen={showProgramEditor}
                      className={"add_event_program_full_modal_popup"}
                      onDidDismiss={() => setShowProgramEditor(false)}>
                <IonHeader className="job_detail_sheet_header sheet_modal_header">
                    <IonToolbar>
                        <IonTitle>Detailed Program (HTML)</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent className="ion-padding">
                    <div className="program-editor-wrap">
                        {/* Left: raw HTML editor */}
                        <div className="editor-pane">
                            <Editor
                                height="70vh"
                                defaultLanguage="html"
                                theme="vs-light"
                                value={form.program_html}
                                onChange={(value) => handleChange('program_html', value || '')}
                            />
                        </div>

                        {/* Right: live preview */}
                        <div className="preview-pane">
                            <div
                                className="preview-inner"
                                dangerouslySetInnerHTML={{
                                    __html: form.program_html?.trim()
                                        ? form.program_html
                                        : '<p><em>Live preview…</em></p>'
                                }}
                            />
                        </div>
                    </div>

                    <div className="editor-actions">
                        <IonButton onClick={() => setShowProgramEditor(false)} expand="block">
                            Done
                        </IonButton>
                    </div>
                </IonContent>
            </IonModal>
        </>
    );
}
